package top.wcpe.allincore.ui.runtime.event

import kotlin.jvm.JvmOverloads

/**
 * 键盘按键事件。
 *
 * DSL 作者在 `onKey { event -> ... }` handler 内可调用 [consume] 标记本事件已被消费——框架据此决定
 * 是否阻止 vanilla 继续处理（如移动 / ESC 等）。未调用 [consume] 时事件仅被「监听」，不影响 vanilla 行为。
 *
 * @param key 字符串键名（PascalCase 风格，见 [top.wcpe.allincore.common.input.key.Keys]；未识别键为空字符串）
 * @param scanCode 原始扫描码
 * @param modifiers 修饰键位掩码（Shift/Ctrl/Alt 等）
 * @param action 动作：0=release，1=press，2=repeat
 * @param vanillaKey 当前玩家 keymap 中该物理键绑定的 translation key（如 `"key.swapOffhand"`）；
 *                   非 fabric 平台或未命中时为空字符串。DSL 可写
 *                   `if (event.vanillaKey == "key.swapOffhand") event.consume()` 实现「按玩家自定义按键」
 *                   拦截，无须关心 keyCode。
 * @param holdMs 按键已按住的累计毫秒数：press（action=1）始终为 0；repeat（action=2）/ release（action=0）
 *               为本次按下到当前事件的时间差。平台层维护「按键 → 按下时间戳」表，未跟踪的物理键回落为 0。
 */
class KeyEvent
@JvmOverloads
constructor(
    key: String,
    scanCode: Int,
    modifiers: Int,
    action: Int,
    // 旧 5 参 / 4 参构造由 @JvmOverloads 自动派生：vanillaKey 缺省空字符串、holdMs 缺省 0，兼容旧调用方
    vanillaKey: String? = "",
    holdMs: Long = 0L,
) {

    private val key: String = key
    private val scanCode: Int = scanCode
    private val modifiers: Int = modifiers
    private val action: Int = action

    // vanillaKey 为 null 时归一为空字符串，避免下游空判断
    private val vanillaKey: String = vanillaKey ?: ""

    // holdMs 负值钳为 0，未跟踪的物理键平台层回落为 0
    private val holdMs: Long = maxOf(holdMs, 0L)

    /**
     * 是否已被消费。Java 侧以 `isConsumed()` 方法式查询，Kotlin 侧以 `event.isConsumed` 属性式读取，
     * 仅可经 [consume] 置位。
     */
    var isConsumed: Boolean = false
        private set

    fun key(): String = key

    fun scanCode(): Int = scanCode

    fun modifiers(): Int = modifiers

    fun action(): Int = action

    /** 当前玩家 keymap 中该物理键绑定的 translation key；未命中返回空字符串。 */
    fun vanillaKey(): String = vanillaKey

    /** 累计按住毫秒数：press=0；repeat / release 为按下到当前事件的时间差；未跟踪的键回落为 0。 */
    fun holdMs(): Long = holdMs

    /**
     * DSL 作者在 handler 内调用：标记本事件已被消费，阻止 vanilla 继续处理。
     *
     * 典型用法：
     * ```
     * onKey { event ->
     *     if (event.vanillaKey == "key.swapOffhand") {
     *         event.consume()
     *     }
     * }
     * ```
     */
    fun consume() {
        isConsumed = true
    }
}
